import { useLingui as useLinguiI18n } from "@lingui/react";
import { useLingui } from "@lingui/react/macro";
import { LogOut, UserPlusIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";

import { SidebarTrigger } from "@/components/ui-kit/base/sidebar";

import logoPng from "../../assets/sq_logo.png";
import { Button } from "../../components/ui-kit/button";
import spinPng from "../../components/ui-kit/sq_spin.png";
import { PatientSearch } from "../../features/patients/patient-search";
import { AppModel } from "../app-model";
import { pageTitles } from "../page-titles";

export const MainHeader = observer(function MainHeader({
  app,
}: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const { i18n } = useLinguiI18n();

  return (
    <div
      data-slot="main-header"
      // Its own --top-bar tokens rather than the rail's: the design tints this
      // surface apart from the white rail. In dark mode they resolve to the
      // same value, so the two merge again there.
      // Tighter side padding below the nav breakpoint, where the row starts
      // with the drawer trigger rather than the wordmark — 22px would leave it
      // stranded away from the edge.
      //
      // Three equal-outer-column grid above that breakpoint, so the search in
      // the middle track sits on the header's true centre line: `1fr` either
      // side means the two outer tracks always measure the same, whatever the
      // wordmark and the account controls happen to need. A flex row with the
      // search centred in the space left over between them would instead put it
      // ~75px right of centre, since the controls are much wider than the
      // wordmark — the same trap the mobile wordmark below sidesteps with
      // absolute positioning. `minmax(0,…)` rather than a bare `1fr` so a track
      // can shrink under its content and let that content truncate, instead of
      // widening and pushing the middle off-centre.
      className="border-top-bar-border bg-top-bar text-sidebar-foreground sticky top-0 z-40 flex h-[52px] items-center gap-2 border-b px-2 text-[13px] nav:grid nav:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] nav:gap-4 nav:px-[22px]"
    >
      {/* Below the nav breakpoint the sidebar is a drawer, so it needs a
          trigger out here; on desktop it toggles from its own footer. */}
      <SidebarTrigger className="text-sidebar-foreground/70 not-aria-disabled:hover:bg-sidebar-accent hover:text-sidebar-accent-foreground nav:hidden" />

      {/* Lives here, not in the sidebar: stays put regardless of the
          sidebar's collapsed/expanded state. Below the nav breakpoint there's
          a lot less room next to the drawer trigger, so the wordmark shrinks
          to the same mark the loading spinner uses.

          Centred on mobile by taking it out of the flow entirely — centring it
          within the row would only centre it in the space left over between the
          trigger and the account controls, which is not the middle of the
          header. Absolute keeps it on the header's true centre line whatever
          those two sides happen to measure. */}
      <Link
        to="/"
        className="flex items-center max-nav:absolute max-nav:left-1/2 max-nav:-translate-x-1/2 nav:-m-2"
      >
        <img
          src={logoPng}
          alt="Seerlinq"
          className="hidden w-[130px] nav:block"
        />
        <img src={spinPng} alt="Seerlinq" className="size-8 nav:hidden" />
      </Link>

      {/* Here rather than on the patients list it started on: reachable from
          every route, so ⌘K works wherever you are. Wrapped so the grid's middle
          track is one element: `PatientSearch` renders its trigger beside a
          dialog root, and though that root puts nothing in the flow, it should
          not be the grid that depends on it. */}
      <div className="flex min-w-0 justify-center">
        <PatientSearch model={app.patientSearch} />
      </div>

      {/* 14px gap between the name and the action, per the design.
          `min-w-0` + a truncating name: a long username must give way rather
          than push the logout button off the bar. */}
      <div className="flex min-w-0 grow items-center justify-end gap-[14px]">
        {/* The highest-emphasis control in the chrome, so it is the primary.
            The logout button below steps back to the chrome's ink because it
            sits beside this, and two loud controls in a magenta-tinted bar
            would compete rather than rank. Label reuses the route's own page
            title, so no new translatable string. */}
        {app.seerlinqApi.canIAddPatients && (
          <Button
            asChild
            variant="primary"
            size="sm"
            className="max-nav:w-8 max-nav:justify-center max-nav:px-0"
          >
            <Link to="/add-patient">
              <UserPlusIcon className="size-4" />
              <span className="max-nav:sr-only">
                {i18n._(pageTitles.addNewPatient)}
              </span>
            </Link>
          </Button>
        )}

        <Link
          to="/account"
          className="text-sidebar-foreground hover:text-sidebar-accent-foreground cursor-pointer truncate hover:underline"
        >
          {app.seerlinqApi.username}
        </Link>

        {/* Recoloured to the chrome's own ink: the bar is the sidebar's
            surface, not the page's, so the page foreground reads too dark. */}
        <Button
          variant="tertiary"
          icon={LogOut}
          label={t`Logout`}
          className="text-sidebar-foreground/70 not-aria-disabled:hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          onClick={() => void app.seerlinqApi.logout()}
        />
      </div>
    </div>
  );
});
